import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import "../styles/productDetailPopup.css";

export default function ProductDetailPopup({ product, onClose }) {
  const specsRef = useRef(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showScrollBottom, setShowScrollBottom] = useState(true);
  
  // Image carousel state
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const autoPlayRef = useRef(null);

  // Prepare images array - if product has multiple images, use them, otherwise use single image
  const productImages = product.images || [product.image];
  
  // Auto-play functionality
  useEffect(() => {
    if (isAutoPlaying && productImages.length > 1) {
      autoPlayRef.current = setInterval(() => {
        setCurrentImageIndex((prevIndex) => 
          prevIndex === productImages.length - 1 ? 0 : prevIndex + 1
        );
      }, 4000); // Change image every 4 seconds
    }

    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, [isAutoPlaying, productImages.length]);

  // Pause auto-play on hover
  const handleMouseEnter = () => {
    setIsAutoPlaying(false);
  };

  const handleMouseLeave = () => {
    setIsAutoPlaying(true);
  };

  // Navigation functions
  const goToPrevious = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === 0 ? productImages.length - 1 : prevIndex - 1
    );
    setIsAutoPlaying(false); // Pause auto-play on manual navigation
    // Resume auto-play after 5 seconds of inactivity
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };

  const goToNext = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === productImages.length - 1 ? 0 : prevIndex + 1
    );
    setIsAutoPlaying(false); // Pause auto-play on manual navigation
    // Resume auto-play after 5 seconds of inactivity
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };

  const goToSlide = (index) => {
    setCurrentImageIndex(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };

  useEffect(() => {
    if (!product) return;

    const handleEsc = (e) => {
      if (e.key === "Escape") onClose();
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleEsc);

    return () => {
      document.body.style.overflow = "auto";
      document.removeEventListener("keydown", handleEsc);
    };
  }, [product, onClose]);

  useEffect(() => {
    const checkScroll = () => {
      if (specsRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = specsRef.current;
        const isAtTop = scrollTop === 0;
        const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;
        
        setShowScrollTop(!isAtTop);
        setShowScrollBottom(!isAtBottom);
      }
    };

    const specsElement = specsRef.current;
    if (specsElement) {
      specsElement.addEventListener('scroll', checkScroll);
      setTimeout(checkScroll, 100);
    }

    return () => {
      if (specsElement) {
        specsElement.removeEventListener('scroll', checkScroll);
      }
    };
  }, [product]);

  const scrollToTop = () => {
    if (specsRef.current) {
      specsRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  const scrollToBottom = () => {
    if (specsRef.current) {
      specsRef.current.scrollTo({
        top: specsRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  if (!product) return null;

  return ReactDOM.createPortal(
    <div
      className="product-modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="product-modal-title"
    >
      <div className="product-modal-container">
        {/* Close Button */}
        <button
          className="product-modal-close"
          onClick={onClose}
          aria-label="Close modal"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path
              d="M18 6L6 18M6 6l12 12"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {/* Modal Content */}
        <div className="product-modal-content">
          <div className="product-modal-grid">
            {/* Image Section with Carousel */}
            <div className="product-modal-image-wrapper">
              <div 
                className="image-container carousel-container"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                {/* Main Image */}
                <img
                  src={productImages[currentImageIndex]}
                  alt={`${product.name} - View ${currentImageIndex + 1}`}
                  className="product-modal-image"
                  loading="lazy"
                />

                {/* Navigation Arrows - Only show if multiple images */}
                {productImages.length > 1 && (
                  <>
                    <button 
                      className="carousel-arrow carousel-arrow-left"
                      onClick={goToPrevious}
                      aria-label="Previous image"
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                    <button 
                      className="carousel-arrow carousel-arrow-right"
                      onClick={goToNext}
                      aria-label="Next image"
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>

                    {/* Auto-play indicator */}
                    <div className="auto-play-indicator">
                      <div className={`auto-play-dot ${isAutoPlaying ? 'playing' : 'paused'}`}></div>
                    </div>
                  </>
                )}

                {/* Image Counter */}
                {productImages.length > 1 && (
                  <div className="image-counter">
                    <span className="current-index">{currentImageIndex + 1}</span>
                    <span className="total-images">/{productImages.length}</span>
                  </div>
                )}

                {/* Category Badge */}
                <span className="product-badge">
                  {product.category}
                </span>
              </div>

              {/* Thumbnail Navigation - Only show if multiple images */}
              {productImages.length > 1 && (
                <div className="thumbnail-container">
                  {productImages.map((img, index) => (
                    <button
                      key={index}
                      className={`thumbnail-button ${index === currentImageIndex ? 'active' : ''}`}
                      onClick={() => goToSlide(index)}
                      aria-label={`Go to image ${index + 1}`}
                    >
                      <img 
                        src={img} 
                        alt={`Thumbnail ${index + 1}`}
                        className="thumbnail-image"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Details Section */}
            <div className="product-modal-details">
              <div className="product-header">
                <h1 id="product-modal-title" className="product-title">
                  {product.name}
                </h1>
                <div className="product-subtitle">
                  Premium Quality Paper Board
                </div>
              </div>

              <div className="specifications-section">
                <div className="section-header">
                  <h3 className="spec-title">
                    <span className="spec-icon">📋</span>
                    Product Specifications
                  </h3>
                  {/* Scroll Indicator Header */}
                  <div className="scroll-indicator-header">
                    <span className="scroll-count">
                      {Object.keys(product.details || {}).length} specifications
                    </span>
                    <div className="scroll-indicator-dots">
                      {showScrollBottom && (
                        <span className="scroll-hint">Scroll for more ↓</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Specifications List with Scroll */}
                <div className="spec-list-container" ref={specsRef}>
                  <div className="spec-list">
                    {Object.entries(product.details || {}).map(
                      ([key, value], index) => (
                        <div key={key} className="spec-item">
                          <div className="spec-key-wrapper">
                            <span className="spec-key">{key}</span>
                          </div>
                          <div className="spec-value-wrapper">
                            <span className="spec-value">{value}</span>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}